import React, { useState, useRef, useEffect } from "react";

// Soft UI Dashboard React components

// @mui material components & icons
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftAvatar from "components/SoftComponents/SoftAvatar";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftTypography from "components/SoftComponents/SoftTypography";
import { Box, Grid } from "@mui/material";
import { PhotoProvider, PhotoView } from "react-photo-view";

export default function ImageUploadMenu({ field, values, setFieldValue, deviceIndex }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const cameraInputRef = useRef(null);
    const libraryInputRef = useRef(null);
    const valuesRef = useRef(values);

    useEffect(() => {
        valuesRef.current = values;
    }, [values]);

    const isMenuOpen = Boolean(anchorEl);

    const handleOpenMenu = (event) => {
        // Prevent event bubbling if clicking the container directly
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);

        // map directly to objects without reading into ArrayBuffers
        const newFiles = files.map((file) => ({
            file,
            previewUrl: URL.createObjectURL(file),
        }));

        setFieldValue(field.name, [...values, ...newFiles]);
        e.target.value = "";
    };

    const handleDelete = (indexToDelete) => {
        // free up memory when user deletes a specific image
        if (values[indexToDelete]?.previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(values[indexToDelete].previewUrl);
        }

        const updatedImages = values.filter((_, index) => index !== indexToDelete);
        setFieldValue(field.name, updatedImages);
    };

    // cleanup object URLs when the component unmounts
    useEffect(() => {
        return () => {
            valuesRef.current.forEach((image) => {
                if (image.previewUrl?.startsWith('blob:')) {
                    URL.revokeObjectURL(image.previewUrl);
                }
            });
        };
    }, []);

    return (
        <SoftBox display="flex" justifyContent="center" alignItems="center">
            {/* Hidden Native File Inputs */}
            <input
                type="file"
                accept="image/*"
                capture="environment"
                ref={cameraInputRef}
                onChange={handleImageChange}
                style={{ display: "none" }}
            />
            <input
                type="file"
                accept="image/*"
                multiple
                ref={libraryInputRef}
                onChange={handleImageChange}
                style={{ display: "none" }}
            />

            {/* Main Soft UI Boxed Container */}
            <SoftBox
                position="relative"
                width="100%"
                borderRadius="xl"
                bgColor="white"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                onClick={!values.length ? handleOpenMenu : undefined}
                sx={{
                    border: "2px dashed #cbd5e1",
                    cursor: "pointer",
                    overflow: "hidden",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                        shadow: "lg",
                        borderColor: values.length ? "none" : "#a0aec0",
                    },
                }}
            >
                {values.length ? (
                    /* State 1: Photo Preview with Top-Right Icon */
                    <SoftBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        width="100%"
                    >
                        <Box p={2.5} sx={{ width: "calc(100% - 40px)" }}>
                            <PhotoProvider id={'inspection_images'}>
                                <Grid container spacing={1}>
                                    {values.length && (
                                        <>
                                            {values.map((image, index) => (
                                                <Grid item xxs={6} lg={4} x3l={2} key={index}>
                                                    <SoftBox display="flex" flexDirection="column" justifyContent="flex-end" alignItems="center" sx={{ height: '100%', gap: 1 }}>
                                                        <PhotoView src={image.previewUrl}>
                                                            <SoftBox sx={{ maxHeight: '200px' }}>
                                                                <SoftBox component={'img'} src={image.previewUrl} alt={`preview ${index}`} style={{ maxWidth: '100%', maxHeight: 'inherit', border: '1px solid black', objectFit: 'contain' }} />
                                                            </SoftBox>
                                                        </PhotoView>
                                                        <SoftButton size="small" fullWidth color="error" onClick={() => handleDelete(index)}>
                                                            Delete
                                                        </SoftButton>
                                                    </SoftBox>
                                                </Grid>
                                            ))}
                                        </>
                                    )}
                                </Grid>
                            </PhotoProvider>
                        </Box>
                        <SoftBox
                            position="absolute"
                            top={12}
                            right={12}
                            zIndex={1}
                            width="48px"
                            height="48px"
                            borderRadius="48px"
                            bgColor="dark"
                            shadow="md"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            onClick={handleOpenMenu}
                            sx={{
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    transform: "scale(1.08)",
                                    shadow: "lg",
                                },
                            }}
                        >
                            <Icon fontSize="small" sx={{ color: "#fff" }}>
                                add_a_photo
                            </Icon>
                        </SoftBox>
                    </SoftBox>
                ) : (
                    /* State 2: Empty Box with Centered Add Icon */
                    <SoftBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        width="100%"
                        gap={1}
                        py={5}
                    >
                        <SoftBox
                            width="48px"
                            height="48px"
                            borderRadius="12px"
                            bgColor="grey-100"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Icon fontSize="medium" color="secondary">
                                add_a_photo
                            </Icon>
                        </SoftBox>
                        <SoftTypography variant="caption" fontWeight="medium" color="text">
                            Add {deviceIndex == 0 ? 'Inspection' : 'Rectification'} Photos
                        </SoftTypography>
                    </SoftBox>
                )}
            </SoftBox>

            {/* Soft UI Styled Dropdown Menu */}
            <Menu
                id="boxed-upload-menu"
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleCloseMenu}
                anchorOrigin={{
                    vertical: "center",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: values.length ? "right" : "left",
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        borderRadius: "1rem",
                        boxShadow: "0 10px 30px 0 rgba(0,0,0,0.12)",
                        padding: "0.5rem",
                    },
                }}
            >
                <MenuItem
                    onClick={() => {
                        cameraInputRef.current?.click();
                        handleCloseMenu();
                    }}
                    sx={{ borderRadius: "0.5rem", py: 1.2, px: 2 }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1.5}>
                        <Icon color="action">camera_alt</Icon>
                        <SoftTypography variant="button" fontWeight="medium" color="text">
                            Take Photo
                        </SoftTypography>
                    </SoftBox>
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        libraryInputRef.current?.click();
                        handleCloseMenu();
                    }}
                    sx={{ borderRadius: "0.5rem", py: 1.2, px: 2 }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1.5}>
                        <Icon color="action">photo_library</Icon>
                        <SoftTypography variant="button" fontWeight="medium" color="text">
                            Choose from Library
                        </SoftTypography>
                    </SoftBox>
                </MenuItem>
            </Menu>
        </SoftBox>
    );
}